import React, { useState, useEffect } from "react";
import { Mail, Copy, Shuffle, Link2, Link, Check, X, Languages, ArrowLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../../../Components/UI/card";
import Upcoming from "../../../Components/Common/Upcoming";
import { AlertBox } from "../../../Components/Common/AlertBox";
import Policies from "../Policies/Policies";
import useIsMobile from "../../../Hooks/useIsMobile";
import { setInquiryDuplication, setPreferredLanguage } from "../Policies/PoliciesAPI";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogClose
} from "../../../Components/UI/dialog";
import { Button } from "../../../Components/UI/button";
import { getConfigData } from "../../../Utils/Storage";
import { getMobileHeaderTitle } from "../../../Utils/Utilities";
import { cn } from '../../../Lib/Cn';
import { getAgencyDetails } from "../../../Store/Reducers";

const cardData = [
  // {
  //   id: 'assignment',
  //   icon: Shuffle,
  //   title: "Auto Assignment",
  //   description: "Assign inquiries automatically by selecting a Sales Executive from the list.",
  //   component: "Upcoming",
  // },
  {
    id: 'duplication',
    icon: Copy,
    title: "Duplication",
    description: "Do you want to allow duplicate leads?",
    component: "Duplication",
    tags: ['duplicate', 'leads', 'inquiries', 'mobile', 'email', 'policy', 'rules', 'prevention', 'restrict', 'allow duplicate', 'merge', 'contacts']
  },
  {
    id: 'language',
    icon: Languages,
    title: "Recording Language",
    description: "Select the language used to detect and transcribe your interaction notes.",
    component: "LanguageSelection",
    tags: ['voice', 'audio', 'notes', 'speech', 'transcribe', 'translation', 'recordings', 'hindi', 'english', 'gujarati', 'marathi', 'sound', 'dictation', 'speech to text']
  },
];

const PoliciesPage = ({ searchTerm = "" }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [duplicationDialogOpen, setDuplicationDialogOpen] = useState(false);
  const [emailConfigOpen, setEmailConfigOpen] = useState(false);
  const [googleRevLinkOpen, setGoogleRevLinkOpen] = useState(false);
  const isMobile = useIsMobile();
  const allowDupInq = useSelector(state => state.masterData.allowDupInq);
  const agencyDetails = useSelector(getAgencyDetails);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");

  // Whitelisted Agency IDs for Recording Language
  const languageWhitelistedAgencies = [1, 10682, 41098, 50];

  useEffect(() => {
    const configData = getConfigData();
    if (configData?.PrefLang) {
      setSelectedLanguage(configData.PrefLang);
    }
  }, [languageDialogOpen]);

  const popularLanguages = [
    "English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Kannada", "Bengali", "Punjabi"
  ];

  const handleDuplicationChange = async (value) => {
    try {
      await setInquiryDuplication(value);
      setDuplicationDialogOpen(false);
      toast.success("Duplicate policy for inquiries updated.");
    } catch (error) {
      console.error("Error setting duplication:", error);
    }
  };

  const handleCardClick = (card) => {
    if (card.component === "Duplication") {
      setDuplicationDialogOpen(true);
    } else if (card.component === "LanguageSelection") {
      setLanguageDialogOpen(true);
    } else if (card.component === "Upcoming") {
      setDialogOpen(true);
    }
  };

  const handleLanguageSave = async () => {
    try {
      await setPreferredLanguage(selectedLanguage);
      toast.success(`Recording language set to ${selectedLanguage}`);
      setLanguageDialogOpen(false);
    } catch (error) {
      console.error("Error setting language:", error);
      toast.error("Failed to update language preference.");
    }
  };

  const query = (searchTerm || "").toLowerCase().trim();
  const visibleCards = cardData
    .filter(card => card.id !== 'language' || languageWhitelistedAgencies.includes(agencyDetails?.AgencyID))
    .filter(card => {
      if (!query) return true;
      const titleMatch = card.title?.toLowerCase().includes(query);
      const descMatch = card.description?.toLowerCase().includes(query);
      const tagsMatch = card.tags?.some(tag => tag.toLowerCase().includes(query) || query.includes(tag.toLowerCase()));
      return titleMatch || descMatch || tagsMatch;
    });

  if (visibleCards.length === 0 && query) {
    return null;
  }

  return (
    <>
      <div className="bg-[var(--bg-grey)]">
        {isMobile ? (
          <div className="bg-white rounded-xl border border-gray-200/70 shadow-2xs overflow-hidden">
            {visibleCards.map((card, index, arr) => {
                const IconComponent = card.icon;
                const subtext = card.id === 'duplication'
                  ? (allowDupInq ? 'Allowed' : 'Disallowed')
                  : card.id === 'language'
                  ? selectedLanguage
                  : '';

                return (
                  <div
                    key={card.id}
                    onClick={() => handleCardClick(card)}
                    className="relative flex items-center justify-between px-4 py-4 hover:bg-gray-50/70 active:bg-gray-100/70 transition-colors cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-3.5 min-w-0 flex-1">
                      <IconComponent className="w-[18px] h-[18px] text-gray-700 stroke-[1.8] shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-[14px] font-medium text-gray-900 leading-tight truncate">
                          {card.title}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {subtext && (
                        <span className="text-[13px] text-gray-400">
                          {subtext}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400 stroke-[2] shrink-0" />
                    </div>

                    {/* Centered non-full-width divider */}
                    {index < arr.length - 1 && (
                      <div className="absolute bottom-0 left-4 right-4 h-[1px] bg-gray-100" />
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {visibleCards.map((card) => {
              const IconComponent = card.icon;
              return (
                <Card
                  key={card.id}
                  className={`sahi-card !shadow-[0_2px_6px_rgba(0,0,0,0.04)] !rounded-xl !pb-3 cursor-pointer`}
                  onClick={() => handleCardClick(card)}
                >
                  <CardContent className="sahi-card-content !gap-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mb-1 bg-sahi-primary-color">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-text-color ${isMobile ? "text-[14px]" : "text-lg"} mb-1 leading-tight`}>
                        {card.title}
                      </h3>
                      <p className={`text-zinc-500 ${isMobile ? "text-[12px]" : "text-sm"} leading-relaxed overflow-hidden text-ellipsis line-clamp-2`}>
                        {card.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Upcoming open={dialogOpen} onOpenChange={setDialogOpen} />
        <Policies open={emailConfigOpen} onOpenChange={setEmailConfigOpen} configType="EMAIL" />
        <Policies open={googleRevLinkOpen} onOpenChange={setGoogleRevLinkOpen} configType="GOOGLE_REVIEW_LINK" />

        <AlertBox
          open={duplicationDialogOpen}
          onOpenChange={setDuplicationDialogOpen}
          title="Duplication"
          content={
            <div>
              <p className="mt-1 text-sm text-[var(--sub-text-color)] leading-snug">
                Do you wish to allow import of inquiries that have the same mobile/email as existing inquiries?
              </p>
              <div className="mt-4 rounded-md">
                <p className="text-sm font-medium text-gray-700">
                  Current Preference: <span className={`font-semibold ${allowDupInq ? 'text-sahi-green' : 'text-sahi-red'}`}>
                    {allowDupInq ? 'Allowed' : 'Disallowed'}
                  </span>
                </p>
              </div>
            </div>
          }
          actions={[
            { label: "Cancel", variant: "cancel", value: "cancel", className: "w-full sm:w-auto", icon: <X className="h-4 w-4" /> },
            { label: "No (Disallow)", variant: "action", value: 0, className: "w-full sm:w-auto", icon: <X className="h-4 w-4" /> },
            { label: "Yes (Allow)", variant: "action", value: 1, className: "w-full sm:w-auto", icon: <Check className="h-4 w-4" /> },
          ]}
          onAction={(actionValue) => {
            if (actionValue === 1) handleDuplicationChange(1);
            else if (actionValue === 0) handleDuplicationChange(0);
            else setDuplicationDialogOpen(false);
          }}
        />
      </div>
      <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="lg:max-w-[40vw] max-w-[100vw]">
          <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {isMobile && (
                  <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                    <ArrowLeft className="h-6 w-6" />
                  </DialogClose>
                )}
                <DialogTitle className="text-lg font-semibold">
                  {isMobile ? getMobileHeaderTitle("Recording Language") : "Recording Language"}
                </DialogTitle>
              </div>
              {!isMobile && (
                <DialogClose>
                  <X className="h-5 w-5 text-gray-500" />
                </DialogClose>
              )}
            </div>
          </DialogHeader>
          <div className="px-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              {popularLanguages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setSelectedLanguage(lang)}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${selectedLanguage === lang
                    ? "border-sahi-primary-color bg-white text-sahi-primary-color font-bold ring-1 ring-sahi-primary-color"
                    : "border-gray-200 bg-white  hover:border-gray-300"
                    }`}
                >
                  <span className="truncate text-sm">{lang}</span>
                  {selectedLanguage === lang && <Check className="h-4 w-4 flex-shrink-0" />}
                </button>
              ))}
            </div>
            <div className="pt-4 pb-4">
              <div className="flex gap-4 sm:flex-row sm:justify-start sm:gap-3">
                <Button onClick={handleLanguageSave} className="w-auto"><Check className="h-3 w-3" /> Save</Button>
                <Button variant="secondary" onClick={() => setLanguageDialogOpen(false)}><X className="h-3 w-3 " /> Close</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PoliciesPage;